import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, TrendingUp, Zap, Activity, Repeat, Tags,
  Heart, RotateCcw, CheckCheck, Mail, Clock,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ScreenHeader from "../components/layout/ScreenHeader";
import EmptyState from "../components/EmptyState";

const PERIODS = [
  { key: "today", label: "Hoy" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "all", label: "Total" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("all");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/metrics", { params: { period } });
      setMetrics(data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (forbidden || (user && user.role !== "admin")) {
    return (
      <div data-testid="admin-forbidden">
        <ScreenHeader eyebrow="Acceso restringido" title="Solo owner / admin" subtitle="Esta sección es interna." />
        <div className="px-6 mt-4">
          <button
            onClick={() => navigate("/")}
            className="text-sage-700 font-bold text-sm inline-flex items-center gap-1.5"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={14} /> Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const pct = (n) => `${Math.round((n || 0) * 100)}%`;

  return (
    <div data-testid="admin-dashboard">
      <ScreenHeader
        eyebrow="Owner"
        title="FiPer · Pulso"
        subtitle="Cómo crece y se activa el producto."
      />

      <div className="px-6 mb-4 flex gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            data-testid={`admin-period-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`px-3 h-9 rounded-full text-xs font-bold ${
              period === p.key
                ? "bg-sage-900 text-bone"
                : "bg-white border border-line text-soft"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading || !metrics ? (
        <div className="px-6"><div className="rounded-3xl bg-white border border-line p-6 animate-pulse h-40" /></div>
      ) : (
        <>
          {/* Crecimiento */}
          <Section title="Crecimiento" icon={TrendingUp}>
            <Grid>
              <KCard label="Usuarios totales" value={metrics.growth.total_users} testid="m-total-users" />
              <KCard label="Nuevos hoy" value={metrics.growth.new_users_today} testid="m-new-today" />
              <KCard label="Nuevos 7d" value={metrics.growth.new_users_7d} testid="m-new-7d" />
              <KCard label="Nuevos 30d" value={metrics.growth.new_users_30d} testid="m-new-30d" />
              <KCard label="WAU (activos 7d)" value={metrics.growth.wau} testid="m-wau" />
              <KCard label="MAU (activos 30d)" value={metrics.growth.mau} testid="m-mau" />
            </Grid>
            <Sparkline data={metrics.growth.signups_daily} />
          </Section>

          {/* Activación */}
          <Section title="Activación" icon={Zap}>
            <Grid>
              <KCard label="1ra cuenta" value={metrics.activation.users_with_account} sub={pct(metrics.activation.rate_first_account)} testid="m-first-account" />
              <KCard label="1er movimiento" value={metrics.activation.users_with_first_tx} sub={pct(metrics.activation.rate_first_tx)} testid="m-first-tx" />
              <KCard label="3+ movimientos" value={metrics.activation.users_with_3plus_tx} sub={pct(metrics.activation.rate_3plus_tx)} testid="m-3plus-tx" />
              <KCard
                label="Hrs signup → 1er tx"
                value={metrics.activation.avg_hours_signup_to_first_tx ?? "—"}
                sub="promedio"
                testid="m-ttf"
              />
            </Grid>
            <Funnel
              total={metrics.growth.total_users}
              steps={[
                { label: "Registro", n: metrics.growth.total_users },
                { label: "1ra cuenta", n: metrics.activation.users_with_account },
                { label: "1er movimiento", n: metrics.activation.users_with_first_tx },
                { label: "3+ movimientos", n: metrics.activation.users_with_3plus_tx },
              ]}
            />
          </Section>

          {/* Uso */}
          <Section title="Uso de producto" icon={Activity}>
            <Grid>
              <KCard label="Con recurrencias activas" value={metrics.usage.users_with_active_recurrence} icon={Repeat} testid="m-recurrences" />
              <KCard label="Pagaron desde Inicio" value={metrics.usage.users_paid_recurrence_from_home} icon={CheckCheck} testid="m-paid-from-home" />
              <KCard label="Categorías custom" value={metrics.usage.users_with_custom_category} icon={Tags} testid="m-categories" />
              <KCard label="En espacio compartido" value={metrics.usage.users_in_shared_space} icon={Heart} testid="m-shared" />
              <KCard label="Invitaciones enviadas" value={metrics.usage.invites_sent} icon={Mail} testid="m-invites-sent" />
              <KCard label="Invitaciones aceptadas" value={metrics.usage.invites_accepted} icon={CheckCheck} testid="m-invites-accepted" />
              <KCard label="Meses reabiertos" value={metrics.usage.months_reopened_total} icon={RotateCcw} testid="m-reopened" />
              <KCard
                label="Tx promedio / activo"
                value={metrics.usage.avg_tx_per_active_user}
                icon={Activity}
                testid="m-avg-tx"
              />
            </Grid>
          </Section>

          <p className="text-[10px] text-soft text-center px-6 mt-2 mb-6">
            Generado {new Date(metrics.generated_at).toLocaleString("es-MX")} · datos agregados sin información personal.
          </p>
        </>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="px-6 mb-5" data-testid={`admin-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-sage-700" />
        <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-sage-700">
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

function KCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div
      className="bg-white border border-line/70 rounded-2xl p-3"
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} className="text-soft" />}
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-soft">{label}</p>
      </div>
      <p className="font-display text-2xl font-extrabold tabular text-ink">{value}</p>
      {sub && <p className="text-[10px] text-soft mt-0.5">{sub}</p>}
    </div>
  );
}

function Funnel({ total, steps }) {
  const base = total || 1;
  return (
    <div className="mt-3 bg-white border border-line/70 rounded-2xl p-3 space-y-2" data-testid="admin-funnel">
      {steps.map((s, i) => {
        const w = Math.max(4, (s.n / base) * 100);
        const ratio = base ? Math.round((s.n / base) * 100) : 0;
        return (
          <div key={s.label}>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-ink font-semibold">{s.label}</span>
              <span className="text-soft tabular">{s.n} · {ratio}%</span>
            </div>
            <div className="h-2 bg-bone rounded-full mt-1">
              <div className="h-2 bg-sage-700 rounded-full" style={{ width: `${w}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Sparkline({ data }) {
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.count)), [data]);
  const w = 280, h = 50, pad = 4;
  const step = (w - pad * 2) / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => [pad + i * step, h - pad - ((d.count / max) * (h - pad * 2))]);
  const dPath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  return (
    <div className="mt-3 bg-white border border-line/70 rounded-2xl p-3" data-testid="admin-signups-sparkline">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-soft">Signups · últimos 14 días</p>
        <p className="text-[10px] text-soft">máx {max}</p>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12">
        <path d={dPath} fill="none" stroke="#16332B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="1.6" fill="#16332B" />
        ))}
      </svg>
    </div>
  );
}
